<template>
  <div>
    <v-data-table
      v-model="itemsToDelete"
      :headers="tableHeaders"
      :items="items"
      :search="search"
      :loading="loading"
      :sort-by="sortBy"
      :mobile-breakpoint="0"
      :height="height"
      :show-select="deletable"
      :hide-default-footer="!paginated"
      :disable-pagination="!paginated"
      :sort-desc="!sortAsc"
      :custom-sort="customSort"
      :custom-filter="customSearch"
      class="main-table"
    >
      <template #[`header.data-table-select`]>
        <div class="d-flex align-center gap-1">
          <div>Eliminar</div>
        </div>
      </template>

      <template #[`item.data-table-select`]="{ item, isSelected, select }">
        <v-simple-checkbox
          :disabled="item.processed || item.processing || loading"
          :value="isSelected"
          @input="select($event)"
        />
      </template>

      <template #[`item.createdAt`]="{ item }">
        {{ formatDateUser(item.createdAt, item.createdBy) }}
      </template>

      <template #[`item.updatedAt`]="{ item }">
        {{ formatDateUser(item.updatedAt, item.updatedBy) }}
      </template>

      <template #[`item.processedAt`]="{ item }">
        {{ formatDateUser(item.processedAt, item.processedBy) }}
      </template>

      <template #[`item.director`]="{ item }">
        {{ formatUser(item.director) }}
      </template>

      <template #[`item.subdirector`]="{ item }">
        {{ formatUser(item.subdirector) }}
      </template>

      <template #[`item.regionalManager`]="{ item }">
        {{ formatUser(item.regionalManager) }}
      </template>

      <template #[`item.manager`]="{ item }">
        {{ formatUser(item.manager) }}
      </template>

      <template #[`item.seller`]="{ item }">
        {{ formatUser(item.seller) }}
      </template>

      <template #[`item.customer`]="{ item }">
        {{ formatUser(item.customer) }}
      </template>

      <template #[`item.doctype`]="{ item }">
        <order-type-tooltip :value="item.doctype" />
      </template>

      <template #[`item.remainingExpirationDays`]="{ item }">
        <div
          v-if="!item.processed"
          :class="getRemainingDaysTextColor(item.remainingExpirationDays)"
        >
          {{ item.remainingExpirationDays }}
        </div>
      </template>

      <template #[`item.select`]="{ item }">
        <action-btn
          type="check"
          color="grey"
          dark
          :disabled="
            item.id === selectedOrderId ||
            item.processed ||
            item.processing ||
            loading
          "
          @onClick="$emit('onSelect', item)"
        />
      </template>

      <template #[`item.total`]="{ item }">
        <div class="text-right">
          {{ formatNumber(item.total) }}
        </div>
      </template>
    </v-data-table>

    <div class="d-flex flex-wrap my-4 gap-2">
      <order-type-tooltip flat :value="orderDoctypes.PRIVATE_CHANNEL" />
      <order-type-tooltip flat :value="orderDoctypes.FIRST_TIME_PURCHASE" />
      <order-type-tooltip flat :value="orderDoctypes.PROMOTIONAL" />
      <v-spacer />
      <slot name="footer-append" />
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment';

  import { OrderDoctypes } from '@/core/constants/orderDoctypeConstants';
  import DateFormats from '@/core/constants/DateFormats';

  import numbersFormats from '@/mixins/numbersFormats';
  import ActionBtn from '@/components/ActionBtn.vue';
  import OrderTypeTooltip from '@/components/tooltips/OrderTypeTooltip.vue';

  export default {
    name: 'RecentOrdersTable',

    components: {
      ActionBtn,
      OrderTypeTooltip,
    },
    mixins: [numbersFormats],
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      search: {
        type: String,
        default: '',
      },
      items: {
        type: Array,
        required: true,
      },
      hideHeaders: {
        type: Array,
        default: () => [],
      },
      height: {
        type: String,
        default: '260px',
      },
      sortBy: {
        type: String,
        default: 'createdAt',
      },
      selectedOrderId: {
        type: Number,
        default: null,
      },

      loading: Boolean,
      paginated: Boolean,
      deletable: Boolean,
      sortAsc: Boolean,
    },
    data() {
      return {
        headers: [
          { value: 'name', text: 'Orden de compra' },
          { value: 'doctype', text: 'Tipo', sortable: false, align: 'center' },
          { value: 'createdAt', text: 'Fecha de creación' },
          { value: 'updatedAt', text: 'Última actualización' },
          { value: 'processedAt', text: 'Fecha de facturación' },
          { value: 'director', text: 'Director' },
          { value: 'subdirector', text: 'Subdirector' },
          { value: 'regionalManager', text: 'Gerente estatal' },
          { value: 'manager', text: 'Gerente' },
          { value: 'seller', text: 'Vendedor' },
          { value: 'customer', text: 'Cliente' },
          { value: 'materialsCount', text: 'N° de unidades', align: 'center' },
          {
            value: 'remainingExpirationDays',
            text: 'Vigencia (días)',
            align: 'center',
          },
          { value: 'total', text: 'Total', align: 'end' },
          {
            value: 'select',
            text: 'Procesar',
            align: 'center',
            sortable: false,
          },
          { value: 'data-table-select', text: 'Eliminar', align: 'end' },
        ],
        orderDoctypes: OrderDoctypes,
      };
    },
    computed: {
      ...mapGetters(['selectedRecentOrder']),

      ...mapGetters('auth', ['channel']),

      itemsToDelete: {
        set(newValue) {
          this.$emit('input', newValue);
        },
        get() {
          return this.value;
        },
      },

      tableHeaders() {
        const hideHeaders = this.deletable ? [] : ['data-table-select'];
        hideHeaders.push(...this.hideHeaders);

        if (hideHeaders.length > 0) {
          return this.headers.filter((h) => !hideHeaders.includes(h.value));
        } else {
          return this.headers;
        }
      },
    },
    methods: {
      getRemainingDaysTextColor(expirationDays) {
        if (expirationDays <= 10) return 'error--text';
        if (expirationDays <= 20) return 'warning--text';
        return 'black--text';
      },

      formatUser(user) {
        return user
          ? `${user.code || ''}${user.name ? ' - ' + user.name : ''}`
          : '';
      },

      formatDateUser(date, user) {
        return date
          ? `${this.formatDate(date)}${user?.name ? ' - ' + user.name : ''}`
          : '';
      },

      formatDate(dateStr) {
        const formatted = moment(dateStr).format(
          DateFormats.DISPLAY_DATETIME_AMPM
        );
        return formatted;
      },

      customSearch(value, search, item) {
        if (value == null || search == null) return false;

        const searchLowerCase = search.toString().toLocaleLowerCase();
        const fields = [
          value,
          this.formatUser(item.director),
          this.formatUser(item.subdirector),
          this.formatUser(item.regionalManager),
          this.formatUser(item.manager),
          this.formatUser(item.seller),
          this.formatUser(item.customer),
          item.createdBy?.name,
          item.updatedBy?.name,
          item.processedBy?.name,
        ];

        return fields.some((field) =>
          field?.toString().toLocaleLowerCase().includes(searchLowerCase)
        );
      },

      customSort(items, index, isDesc) {
        if (!index?.length || isDesc[0] === undefined) {
          return this.items;
        }

        const sortKey = index[0];
        const desc = isDesc[0] ? -1 : 1;

        return items.sort((a, b) => {
          let valA = a[sortKey];
          let valB = b[sortKey];

          if (
            [
              'director',
              'subdirector',
              'regionalManager',
              'manager',
              'seller',
              'customer',
            ].includes(sortKey)
          ) {
            valA = valA?.name || '';
            valB = valB?.name || '';
            return valA.localeCompare(valB) * desc;
          }
          if (['createdAt', 'updatedAt', 'processedAt'].includes(sortKey)) {
            return (new Date(valA) - new Date(valB)) * desc;
          }
          if (typeof valA === 'string' && typeof valB === 'string') {
            return valA.localeCompare(valB) * desc;
          }
          return (valA < valB ? -1 : 1) * desc;
        });
      },
    },
  };
</script>
<style scoped src="@/assets/css/table.css"></style>
